<template>
  <div>
    <DxPopup
      :visible="visible"
      :hide-on-outside-click="false"
      :show-close-button="true"
      :drag-enabled="false"
      :resize-enabled="false"
      :width="400"
      :height="'auto'"
      @hidden="$emit('hidden')"
    >
      <template #title>
        <div class="edit-transaction-title">
          <span v-if="row" class="edit-transaction-title__main"
            >{{ $t("common.edit") }} - {{ row.transaction_type }} / {{ row.product.name }}
          </span>
        </div>
      </template>
      <template #content>
        <div style="padding: 16px; display: flex; flex-direction: column; gap: 16px">
          <div>
            <label class="control-label" style="display: block; margin-bottom: 4px">{{ $t("stock_management.transactions.date") }}</label>
            <DxDateBox
              v-model="form.transaction_date"
              type="date"
              :display-format="getDxGridDefaultDateFormat"
              :class="{ 'field-error': errors.transaction_date }"
              width="100%"
            />
            <small v-if="errors.transaction_date" class="text-danger">{{ errors.transaction_date }}</small>
          </div>
          <div>
            <label class="control-label" style="display: block; margin-bottom: 4px">{{ $t("common.quantity") }}</label>
            <DxNumberBox
              v-model="form.quantity"
              :min="0"
              :showSpinButtons="false"
              class="dx-form-control"
              :class="{ 'field-error': errors.quantity }"
              :format="quantityFormatter"
              placeholder="0.00"
            />
            <small v-if="errors.quantity" class="text-danger">{{ errors.quantity }}</small>
          </div>
          <div style="display: flex; justify-content: flex-end; gap: 8px">
            <SmagriaButton
              :label="$t('common.cancel')"
              :disabled="isSaving"
              :action="hide"
              customClass="secondary border"
              :width="'120px'"
            />
            <SmagriaButton :label="$t('common.save')" :disabled="isSaving" :action="save" customClass="primary" :width="'120px'" />
          </div>
        </div>
      </template>
    </DxPopup>

    <LotQuantityEditorDialog
      v-if="row && row.product && row.product.lot_management"
      :visible="showLotEditor"
      :items="lotSelectorItems"
      :target-quantity="form.quantity"
      :measure-unit-symbol="row && row.product ? row.product.measure_unit_name : ''"
      :product-uuid="row && row.product ? row.product.uuid : null"
      :warehouse-uuid="row && row.source_warehouse ? row.source_warehouse.uuid : null"
      :movement-date="formattedMovementDate"
      :product-name="row && row.product ? row.product.name : ''"
      :transaction-uuid="row ? row.uuid : null"
      @hidden="showLotEditor = false"
      @confirmed="onLotEditorConfirmed"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DxPopup } from "devextreme-vue/popup";
import DxDateBox from "devextreme-vue/date-box";
import DxNumberBox from "devextreme-vue/number-box";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import { format } from "date-fns";
import { getQuantityFormatter } from "~/helpers/stockHelper";
import LotQuantityEditorDialog from "./LotQuantityEditorDialog.vue";

export default {
  name: "EditTransactionDialog",
  components: { DxPopup, DxDateBox, DxNumberBox, SmagriaButton, LotQuantityEditorDialog },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    row: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        transaction_date: null,
        quantity: null,
      },
      errors: {
        transaction_date: "",
        quantity: "",
      },
      isSaving: false,
      showLotEditor: false,
      lotSelectorItems: [],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    quantityFormatter() {
      return getQuantityFormatter(this.row?.product?.measure_unit_name);
    },
    formattedMovementDate() {
      return this.form.transaction_date ? this.formatDateOnly(this.form.transaction_date) : null;
    },
  },
  watch: {
    row(newRow) {
      if (newRow) {
        this.form.transaction_date = newRow.transaction_date ? new Date(newRow.transaction_date) : null;
        this.form.quantity = newRow.quantity ?? null;
        this.errors.transaction_date = "";
        this.errors.quantity = "";
      }
    },
  },
  methods: {
    ...mapActions({
      updateConsumTransaction: "smTransactions/updateConsumTransaction",
    }),
    formatDateOnly(value) {
      if (!value) return value;
      if (typeof value === "string") return value;
      return format(value, "yyyy-MM-dd");
    },
    hide() {
      this.$emit("hidden");
    },
    async save() {
      this.errors.transaction_date = !this.form.transaction_date ? this.$t("validation.field_is_required") : "";
      this.errors.quantity = !(this.form.quantity > 0) ? this.$t("validation.field_is_required") : "";
      if (this.errors.transaction_date || this.errors.quantity) {
        return;
      }

      const formattedDate = this.formatDateOnly(this.form.transaction_date);
      const originalDate = this.row?.transaction_date ? this.formatDateOnly(new Date(this.row.transaction_date)) : null;
      const dateUnchanged = formattedDate === originalDate;
      const quantityUnchanged = this.form.quantity === (this.row?.quantity ?? null);

      if (dateUnchanged && quantityUnchanged) {
        this.$emit("hidden");
        return;
      }

      // If product requires lot management, open lot editor (auto-opens lot selector when empty)
      if (this.row?.product?.lot_management) {
        this.lotSelectorItems = [];
        this.showLotEditor = true;
        return;
      }

      await this.performSave(null);
    },
    async onLotEditorConfirmed(lotItems) {
      this.showLotEditor = false;
      await this.performSave(lotItems);
    },
    async performSave(lotItems) {
      this.isSaving = true;
      const formattedDate = this.formatDateOnly(this.form.transaction_date);
      try {
        const type = this.row?.transaction_type;

        if (type === "Consum") {
          await this.updateConsumTransaction({
            transaction_uuid: this.row.uuid,
            transaction_date: formattedDate,
            quantity: this.form.quantity,
            ...(lotItems ? { lot_items: lotItems } : {}),
          });
        }
        // add else if branches here for other transaction types when needed

        this.$emit("saved");
      } catch (e) {
        this.$emit("save-error", e);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.edit-transaction-title {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.edit-transaction-title__main {
  font-size: 18px;
  font-weight: 700;
  color: #323232;
}
.edit-transaction-title__sub {
  font-size: 14px;
  color: #888;
  margin-top: 2px;
}
</style>
